import * as React from "react"
import { ViewType } from "@digits-graphql/frontend/graphql-public"
import envHelper from "@digits-shared/helpers/envHelper"
import { storage } from "@digits-shared/helpers/storage/storage"
import useSession from "@digits-shared/hooks/useSession"
import { AspectCode } from "@digits-shared/session/AspectCode"
import { getPreferredViewType } from "src/frontend/helpers/getPreferredViewType"
import { useLegalEntityPreference } from "src/frontend/hooks/usePreferenceStore"
import type FrontendSession from "src/frontend/session"

/*
  CONSTANTS
*/
// Legacy storage keys for migration
const OLD_VIEW_TYPE_STORAGE_NAMESPACE = "view-type"
const LEGACY_VIEW_TYPE_STORAGE_NAMESPACE = "stored-view-type"
// Digits on Digits Legal Entity ID
const DIGITS_LEGAL_ENTITY_ID = "e17d700b-b2c0-432b-9678-471d6e66a4b7"

/*
  INTERFACES
*/

interface ViewTypeContextVal {
  viewType: ViewType
  setViewType: (viewType: ViewType) => void
}

/*
  CONTEXT
*/

const ViewTypeContext = React.createContext<ViewTypeContextVal>({
  setViewType: () => {},
  viewType: ViewType.Unknown,
})

/*
  PROVIDER
*/

export const ViewTypeContextProvider: React.FC<React.PropsWithChildren> = ({ children }) => {
  useMigrateLegacyViewType()

  const hasAIBookkeeper = useHasAIBookkeeper()
  const canToggle = useCanToggleViewType()
  const [, setPreference] = useLegalEntityPreference("viewType")
  const effectiveViewType = usePreferredViewType()

  const setViewType = React.useCallback(
    (vt: ViewType) => {
      // Enforce AIBookkeeper access check
      const newVT = !hasAIBookkeeper && vt === ViewType.AIBookkeeper ? ViewType.Ledger : vt
      // Only persist if user can toggle
      if (canToggle) {
        setPreference(newVT)
      }
    },
    [hasAIBookkeeper, canToggle, setPreference]
  )

  const value = React.useMemo(
    () => ({
      viewType: effectiveViewType,
      setViewType,
    }),
    [effectiveViewType, setViewType]
  )

  return <ViewTypeContext.Provider value={value}>{children}</ViewTypeContext.Provider>
}

/*
  HOOKS
 */

export function useViewTypeContext() {
  return React.useContext(ViewTypeContext)
}

/**
 * The view type the session resolves to, read without the context. For code that runs above
 * `ViewTypeContextProvider`, such as the onboarding transitions.
 */
export function usePreferredViewType() {
  const [storedViewType] = useLegalEntityPreference("viewType")
  const session = useSession<FrontendSession>()
  return getPreferredViewType(session, storedViewType)
}

export function useViewType(viewTypeOverride?: ViewType) {
  const { viewType } = useViewTypeContext()

  if (viewTypeOverride) {
    return viewTypeOverride
  }

  return viewType
}

export const useCanToggleViewType = () => {
  const session = useSession<FrontendSession>()
  const { hasAccessToAspect } = session
  const isDoD = session.currentLegalEntityId === DIGITS_LEGAL_ENTITY_ID

  return (
    hasAccessToAspect(AspectCode.AIBookkeeper) &&
    // Only allow digits employees logged in and viewing DoD to toggle view type
    (envHelper.isProduction() ? session.isDigitsEmployee && isDoD : true)
  )
}

/*
  INTERNAL HOOKS
 */

const useHasAIBookkeeper = () => {
  const { currentLegalEntity, sharingViewType } = useSession<FrontendSession>()

  return sharingViewType
    ? sharingViewType === ViewType.AIBookkeeper
    : !!currentLegalEntity?.hasAccessToAspect(AspectCode.AIBookkeeper)
}

/**
 * Migrates view type from legacy localStorage keys to the new preference store.
 * Runs once per legal entity to clean up old storage patterns.
 */
const useMigrateLegacyViewType = () => {
  const session = useSession<FrontendSession>()
  const [currentPref, setPreference] = useLegalEntityPreference("viewType")
  const legalEntityId = session.currentLegalEntityId

  React.useEffect(() => {
    if (!legalEntityId) return

    // Clean up old global key
    const oldGlobalView = storage.local.getItem(OLD_VIEW_TYPE_STORAGE_NAMESPACE)
    if (oldGlobalView) {
      storage.local.removeItem(OLD_VIEW_TYPE_STORAGE_NAMESPACE)
    }

    // Clean up old per-LE key format
    const oldLeKey = `${OLD_VIEW_TYPE_STORAGE_NAMESPACE}-${legalEntityId}`
    const oldLeView = storage.local.getItem(oldLeKey)
    if (oldLeView) {
      storage.local.removeItem(oldLeKey)
    }

    // Migrate from legacy per-LE key to new preference store
    const legacyKey = `${LEGACY_VIEW_TYPE_STORAGE_NAMESPACE}-${legalEntityId}`
    const legacyView = storage.local.getItem(legacyKey)
    if (legacyView && !currentPref) {
      // Migrate the value if we don't have a preference yet
      if (legacyView in ViewType) {
        setPreference(ViewType[legacyView as keyof typeof ViewType])
      }
    }
    // Always clean up the legacy key after migration attempt
    if (legacyView) {
      storage.local.removeItem(legacyKey)
    }
  }, [legalEntityId, currentPref, setPreference])
}
